/**
 * Copyright 2017-2024 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import {connect} from 'react-redux';
import * as portals from 'react-reverse-portal';
import {Rnd} from 'react-rnd';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import {v1 as uuidv1} from 'uuid';

import {raiseWindow, registerWindow, unregisterWindow, setSplitScreen} from '../actions/windows';
import ConfigUtils from '../utils/ConfigUtils';
import LocaleUtils from '../utils/LocaleUtils';
import MiscUtils from '../utils/MiscUtils';
import Icon from './Icon';
import Spinner from './Spinner';

import './style/ResizeableWindow.css';

const WINDOW_GEOMETRIES = {};

class ResizeableWindow extends React.Component {
    static propTypes = {
        baseZIndex: PropTypes.number,
        bottombarHeight: PropTypes.number,
        busyIcon: PropTypes.bool,
        children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
        dockable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        extraControls: PropTypes.arrayOf(PropTypes.shape({
            active: PropTypes.bool,
            icon: PropTypes.string.isRequired,
            callback: PropTypes.func.isRequired,
            msgid: PropTypes.string
        })),
        icon: PropTypes.string,
        initialHeight: PropTypes.number,
        initialWidth: PropTypes.number,
        initialX: PropTypes.number,
        initialY: PropTypes.number,
        initiallyDocked: PropTypes.bool,
        maxHeight: PropTypes.number,
        maxWidth: PropTypes.number,
        maximizeable: PropTypes.bool,
        menuMargins: PropTypes.object,
        minHeight: PropTypes.number,
        minWidth: PropTypes.number,
        minimizeable: PropTypes.bool,
        mobile: PropTypes.bool,
        onClose: PropTypes.func,
        onExternalWindowResized: PropTypes.func,
        onGeometryChanged: PropTypes.func,
        raiseWindow: PropTypes.func,
        registerWindow: PropTypes.func,
        scrollable: PropTypes.bool,
        setSplitScreen: PropTypes.func,
        splitScreenWhenDocked: PropTypes.bool,
        splitTopAndBottomBar: PropTypes.bool,
        title: PropTypes.string,
        titlelabel: PropTypes.string,
        topbarHeight: PropTypes.number,
        unregisterWindow: PropTypes.func,
        usePortal: PropTypes.bool,
        visible: PropTypes.bool,
        windowStacking: PropTypes.array
    };
    static defaultProps = {
        baseZIndex: 10,
        initialX: null,
        initialY: null,
        initialWidth: 240,
        initialHeight: 320,
        minWidth: 50,
        minHeight: 50,
        maxWidth: null,
        maxHeight: null,
        maximizeable: true,
        minimizeable: false,
        visible: true,
        dockable: true,
        onExternalWindowResized: () => {},
        onGeometryChanged: () => {},
        externalWindow: null,
        usePortal: true
    };
    state = {
        geometry: null
    };
    constructor(props) {
        super(props);
        this.rnd = null;
        this.dragShield = null;
        this.id = uuidv1();
        const canvasHeight = window.innerHeight - this.props.bottombarHeight - this.props.topbarHeight;
        const width = Math.min(props.initialWidth, window.innerWidth);
        const height = Math.min(props.initialHeight, canvasHeight);
        if (WINDOW_GEOMETRIES[props.title || props.titlelabel]) {
            this.state.geometry = WINDOW_GEOMETRIES[props.title || props.titlelabel];
        } else {
            this.state.geometry = {
                x: props.initialX !== null ? this.computeInitialX(props.initialX) : Math.max(0, Math.round(0.5 * (window.innerWidth - width))),
                y: props.initialY !== null ? this.computeInitialY(props.initialY) : Math.max(0, Math.round(0.5 * (canvasHeight - height))),
                width: width,
                height: height,
                docked: props.initiallyDocked
            };
        }
        if (props.splitScreenWhenDocked && this.state.geometry.docked) {
            const dockSide = props.dockable === true ? "left" : props.dockable;
            const dockSize = ["left", "right"].includes(dockSide) ? this.state.geometry.width : this.state.geometry.height;
            props.setSplitScreen(this.id, dockSide, dockSize, props.splitTopAndBottomBar);
        }
        this.portalNode = props.usePortal ? portals.createHtmlPortalNode() : null;
    }
    computeInitialX = (x) => {
        return x > 0 || Object.is(x, 0) ? x : window.innerWidth - this.props.initialWidth - Math.abs(x);
    };
    computeInitialY = (y) => {
        const canvasHeight = window.innerHeight - this.props.bottombarHeight - this.props.topbarHeight;
        return y > 0 || Object.is(y, 0) ? y : canvasHeight - this.props.initialHeight - Math.abs(y);
    };
    componentDidMount() {
        this.props.registerWindow(this.id);
        this.props.onGeometryChanged(this.state.geometry);
    }
    componentWillUnmount() {
        this.props.unregisterWindow(this.id);
        if (this.props.splitScreenWhenDocked) {
            this.props.setSplitScreen(this.id, null, null, false);
        }
        if (this.state.externalWindow) {
            this.state.externalWindow.close();
        }
    }
    componentDidUpdate(prevProps, prevState) {
        if (this.rnd && this.props.visible && this.props.visible !== prevProps.visible) {
            this.props.onGeometryChanged(this.state.geometry);
            this.rnd.updatePosition(this.state.geometry);
        }
        if (this.state.geometry !== prevState.geometry) {
            WINDOW_GEOMETRIES[this.props.title || this.props.titlelabel] = this.state.geometry;
        }
        if (this.props.splitScreenWhenDocked && (
            this.props.visible !== prevProps.visible ||
            this.state.geometry !== prevState.geometry ||
            this.state.externalWindow !== prevState.externalWindow
        )) {
            if (
                (!this.props.visible && prevProps.visible) ||
                (this.state.geometry.docked === false && prevState.geometry.docked !== false) ||
                (this.state.geometry.maximized === true && prevState.geometry.maximized !== true) ||
                (this.state.externalWindow && !prevState.externalWindow)
            ) {
                this.props.setSplitScreen(this.id, null, null, false);
            } else if (this.props.visible && this.state.geometry.docked && !this.state.externalWindow && !this.state.geometry.maximized) {
                const dockSide = this.props.dockable === true ? "left" : this.props.dockable;
                const dockSize = ["left", "right"].includes(dockSide) ? this.state.geometry.width : this.state.geometry.height;
                this.props.setSplitScreen(this.id, dockSide, dockSize, this.props.splitTopAndBottomBar);
            }
        }
    }
    renderRole = (role) => {
        return React.Children.toArray(this.props.children).find((child) => child.props.role === role);
    };
    onClose = (ev) => {
        if (this.state.externalWindow) {
            this.state.externalWindow.removeEventListener('beforeunload', this.props.onClose);
        }
        this.props.onClose();
        ev.stopPropagation();
    };
    renderTitleBar = () => {
        const maximized = this.state.geometry.maximized ? true : false;
        const minimized = this.state.geometry.minimized ? true : false;
        const docked = this.state.geometry.docked;
        const dockSide = this.props.dockable === true ? "left" : this.props.dockable;
        let dockable = this.props.dockable && !this.state.externalWindow;
        if (ConfigUtils.getConfigProp("globallyDisableDockableDialogs")) {
            dockable = false;
        }
        let maximizeable = this.props.maximizeable && !this.state.externalWindow;
        if (ConfigUtils.getConfigProp("globallyDisableMaximizeableDialogs")) {
            maximizeable = false;
        }
        const minimizeable = this.props.minimizeable && !this.state.externalWindow;

        let icon = null;
        if (this.props.busyIcon) {
            icon = (<Spinner />);
        } else if (this.props.icon) {
            icon = (<Icon className="resizeable-window-titlebar-icon" icon={this.props.icon} size="large" />);
        }

        let dockIcon = docked ? 'undock' : 'dock';
        dockIcon = dockIcon + "_" + dockSide;

        let detachIcons = null;
        if (!this.props.mobile) {
            detachIcons = this.state.externalWindow ? (
                <Icon className="resizeable-window-titlebar-control" icon="embed" onClick={this.moveToInternalWindow} title={LocaleUtils.tr("window.embed")} />
            ) : (
                <Icon className="resizeable-window-titlebar-control" icon="detach" onClick={this.moveToExternalWindow} title={LocaleUtils.tr("window.detach")} />
            );
        }
        return (
            <div className="resizeable-window-titlebar" onDoubleClick={this.state.externalWindow ? null : this.toggleMaximize}>
                {icon}
                <span className="resizeable-window-titlebar-title">
                    {this.props.title ? LocaleUtils.tr(this.props.title) : (this.props.titlelabel || "")}
                </span>
                {(this.props.extraControls || []).map(entry => {
                    const iconClasses = classnames({
                        "resizeable-window-titlebar-extra-control": true,
                        "resizeable-window-titlebar-extra-control-active": entry.active
                    });
                    return (
                        <Icon
                            className={iconClasses} icon={entry.icon} key={entry.icon}
                            onClick={entry.callback} titlemsgid={entry.msgid} />
                    );
                })}
                {!maximized && dockable ? (
                    <Icon
                        className="resizeable-window-titlebar-control" icon={dockIcon}
                        onClick={this.toggleDock}
                        titlemsgid={this.state.geometry.docked ? LocaleUtils.trmsg("window.undock") : LocaleUtils.trmsg("window.dock")} />
                ) : null}
                {minimizeable ? (
                    <Icon className="resizeable-window-titlebar-control" icon={minimized ? "unminimize" : "minimize"} onClick={this.toggleMinimize} title={minimized ? LocaleUtils.tr("window.unminimize") : LocaleUtils.tr("window.minimize")} />
                ) : null}
                {maximizeable ? (
                    <Icon className="resizeable-window-titlebar-control" icon={maximized ? "unmaximize" : "maximize"} onClick={this.toggleMaximize} title={maximized ? LocaleUtils.tr("window.unmaximize") : LocaleUtils.tr("window.maximize")} />
                ) : null}
                {detachIcons}
                {this.props.onClose ? (
                    <Icon className="resizeable-window-titlebar-control" icon="remove" onClick={this.onClose} title={LocaleUtils.tr("window.close")} />
                ) : null}
            </div>
        );
    };
    renderInternalWindow = () => {
        const maximized = this.state.geometry.maximized ? true : false;
        const minimized = this.state.geometry.minimized ? true : false;
        const docked = this.state.geometry.docked;
        const dockSide = this.props.dockable === true ? "left" : this.props.dockable;
        const splitTopAndBottomBar = this.props.splitTopAndBottomBar && this.props.splitScreenWhenDocked && (docked || maximized);

        const bodyclasses = classnames({
            "resizeable-window-body": true,
            "resizeable-window-body-scrollable": this.props.scrollable,
            "resizeable-window-body-nonscrollable": !this.props.scrollable
        });


        const containerStyle = {
            display: this.props.visible ? 'initial' : 'none',
            left: this.props.menuMargins.left + 'px',
            right: this.props.menuMargins.right + 'px',
            top: splitTopAndBottomBar ? 0 : this.props.topbarHeight + 'px',
            bottom: splitTopAndBottomBar ? 0 : this.props.bottombarHeight + 'px',
            zIndex: splitTopAndBottomBar ? 110 : this.props.baseZIndex + this.props.windowStacking.findIndex(item => item === this.id)
        };

        const windowclasses = classnames({
            "resizeable-window": true,
            "resizeable-window-maximized": maximized,
            "resizeable-window-minimized": minimized,
            "resizeable-window-docked-left": !this.props.splitScreenWhenDocked && this.state.geometry.docked && dockSide === "left" && !maximized,
            "resizeable-window-docked-right": !this.props.splitScreenWhenDocked && this.state.geometry.docked && dockSide === "right" && !maximized,
            "resizeable-window-split-left": this.props.splitScreenWhenDocked && this.state.geometry.docked && dockSide === "left" && !maximized,
            "resizeable-window-split-right": this.props.splitScreenWhenDocked && this.state.geometry.docked && dockSide === "right" && !maximized,
            "resizeable-window-docked-top": this.state.geometry.docked && dockSide === "top" && !maximized,
            "resizeable-window-docked-bottom": this.state.geometry.docked && dockSide === "bottom" && !maximized
        });
        let resizeMode = {
            left: true,
            right: true,
            top: true,
            bottom: true,
            bottomLeft: true,
            bottomRight: true,
            topLeft: true,
            topRight: true
        };
        if (maximized || minimized) {
            resizeMode = false;
        } else if (this.state.geometry.docked) {
            resizeMode = {
                left: dockSide === "right",
                right: dockSide === "left",
                top: dockSide === "bottom",
                bottom: (dockSide !== "bottom" && !this.props.splitScreenWhenDocked) || (this.props.splitScreenWhenDocked && dockSide === "top")
            };
        }
        return (
            <div className="resizeable-window-container" key="InternalWindow" style={containerStyle}>
                <Rnd bounds="parent"
                    className={windowclasses} default={this.state.geometry}
                    disableDragging={maximized || this.state.geometry.docked} enableResizing={resizeMode}
                    maxHeight={this.props.maxHeight || window.innerHeight} maxWidth={this.props.maxWidth || window.innerWidth}
                    minHeight={this.props.minHeight} minWidth={this.props.minWidth}
                    onDragStart={this.onDragStart}
                    onDragStop={this.onDragStop}
                    onMouseDown={() => this.props.raiseWindow(this.id)}
                    onResizeStop={this.onResizeStop}
                    ref={this.initRnd}
                >
                    <div className="resizeable-window-contents">
                        {this.renderTitleBar()}
                        <div className={bodyclasses} onMouseDown={() => this.props.raiseWindow(this.id)}>
                            <div className="resizeable-window-drag-shield" ref={el => {this.dragShield = el;}} />
                            {this.portalNode ? (
                                <div className="resizeable-window-portal-container">
                                    <portals.OutPortal node={this.portalNode} />
                                </div>
                            ) : this.renderRole("body")}
                        </div>
                    </div>
                </Rnd>
            </div>
        );
    };
    renderExternalWindow = () => {
        return ReactDOM.createPortal((
            <div className="resizeable-window-contents">
                {this.renderTitleBar()}
                {this.portalNode ? (
                    <div className="resizeable-window-portal-container">
                        <portals.OutPortal node={this.portalNode} />
                    </div>
                ) : this.renderRole("body")}
            </div>
        ), this.state.externalWindow.document.body);
    };
    render() {
        return [this.portalNode ? (
            <portals.InPortal key="InPortal" node={this.portalNode}>
                {this.renderRole("body")}
            </portals.InPortal>
        ) : null, this.state.externalWindow ? (
            this.renderExternalWindow()
        ) : (
            this.renderInternalWindow()
        )];
    }
    initRnd = (el) => {
        if (el) {
            this.rnd = el;
            this.rnd.updatePosition(this.state.geometry);
        }
    };
    onDragStart = () => {
        if (this.dragShield) {
            this.dragShield.style.display = 'initial';
        }
    };
    onDragStop = (ev, data) => {
        this.setState((state) => ({
            geometry: {...state.geometry, x: data.x, y: data.y}
        }));
        if (this.dragShield) {
            this.dragShield.style.display = 'none';
        }
    };
    onResizeStop = (ev, dir, ref, delta, position) => {
        // Delay one event loop cycle else clientWidth / clientHeight may not yet be up-to-date
        setTimeout(() => {
            this.setState((state) => ({
                geometry: {
                    ...state.geometry,
                    x: position.x,
                    y: position.y,
                    width: ref.clientWidth,
                    height: ref.clientHeight
                }
            }));
        }, 0);
    };
    toggleDock = () => {
        this.setState((state) => ({
            geometry: {
                ...state.geometry,
                docked: !state.geometry.docked
            }
        }));
        this.rnd.updatePosition(this.state.geometry);
    };
    toggleMinimize = () => {
        this.setState((state) => ({
            geometry: {
                ...state.geometry,
                minimized: !state.geometry.minimized
            }
        }));
    };
    toggleMaximize = () => {
        this.setState((state) => ({
            geometry: {
                ...state.geometry,
                maximized: !state.geometry.maximized,
                minimized: false
            }
        }));
    };
    moveToExternalWindow = () => {
        const title = this.props.title ? LocaleUtils.tr(this.props.title) : (this.props.titlelabel || "");
        const windowOptions = [
            "popup=true",
            "toolbar=no",
            "locationbar=no",
            "location=no",
            "directories=no",
            "status=no",
            "menubar=no",
            "scrollbars=yes",
            "resizable=yes",
            "width=" + this.state.geometry.width,
            "height=" + this.state.geometry.height
        ].join(", ");
        const externalWindow = window.open('about:blank', "_blank", windowOptions);
        externalWindow.addEventListener('resize', this.props.onExternalWindowResized, false);
        const loadInterval = setInterval(() => {
            if (externalWindow.document.readyState !== 'complete') {
                return;
            }
            clearInterval(loadInterval);
            externalWindow.addEventListener('beforeunload', this.props.onClose, {capture: false, once: true});
            const titleEl = externalWindow.document.createElement('title');
            titleEl.appendChild(externalWindow.document.createTextNode(title));
            externalWindow.document.head.appendChild(titleEl);

            const icon = MiscUtils.getFaviconFromIcon(this.props.icon, 48);
            if (icon) {
                const iconEl = externalWindow.document.createElement('link');
                iconEl.rel = "icon";
                iconEl.href = icon;
                iconEl.sizes = "48x48";
                externalWindow.document.head.appendChild(iconEl);
            }

            // Inherit styles
            Array.from(document.styleSheets).forEach(styleSheet => {
                if (styleSheet.href) {
                    // External styles
                    const linkElement = externalWindow.document.createElement('link');
                    linkElement.rel = 'stylesheet';
                    linkElement.href = styleSheet.href;
                    externalWindow.document.head.appendChild(linkElement);
                } else if (styleSheet.cssRules) {
                    // Inline styles
                    const styleElement = externalWindow.document.createElement('style');
                    const cssText = Array.from(styleSheet.cssRules).map(rule => rule.cssText).join("\n");
                    styleElement.appendChild(externalWindow.document.createTextNode(cssText));
                    externalWindow.document.head.appendChild(styleElement);
                }
            });

            this.setState({externalWindow: externalWindow});
        }, 50);
    };
    moveToInternalWindow = () => {
        if (this.state.externalWindow) {
            this.state.externalWindow.removeEventListener('beforeunload', this.props.onClose);
            this.state.externalWindow.removeEventListener('resize', this.props.onExternalWindowResized, false);
            this.state.externalWindow.close();
            this.setState({externalWindow: null});
        }
    };
}

export default connect((state) => ({
    windowStacking: state.windows.stacking,
    topbarHeight: state.map.topbarHeight,
    bottombarHeight: state.map.bottombarHeight,
    menuMargins: state.windows.menuMargins,
    mobile: state.browser.mobile
}), {
    raiseWindow: raiseWindow,
    registerWindow: registerWindow,
    setSplitScreen: setSplitScreen,
    unregisterWindow: unregisterWindow
})(ResizeableWindow);
